import { useQuery, type QueryFunctionContext, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type AnyObject } from '@Types';
import { buildTanStackBaseBackRoyalQueryFn, queryClient } from 'BackRoyalApi';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { useConfig } from 'FrontRoyalConfig/useConfig';
import { getCohort } from 'Users';
import { type StudentDashboardShowContents, type StudentDashboardShowParams } from './StudentDashboardContent.types';

const STUDENT_DASHBOARD_QUERY_KEY = 'studentDashboard';
const STUDENT_DASHBOARD_URL = '/student_dashboards/show.json';

type StudentDashboardShowResponse = {
    contents: StudentDashboardShowContents;
    meta: AnyObject;
};

const studentDashboardQueryFn = buildTanStackBaseBackRoyalQueryFn<StudentDashboardShowResponse>();

type StudentDashboardQueryKey = Readonly<[typeof STUDENT_DASHBOARD_QUERY_KEY, StudentDashboardShowParams]>;

const buildQueryKey = (params: StudentDashboardShowParams): StudentDashboardQueryKey =>
    [STUDENT_DASHBOARD_QUERY_KEY, params] as const;

const runStudentDashboardQuery = (
    context: QueryFunctionContext<StudentDashboardQueryKey>,
): Promise<StudentDashboardShowResponse> => {
    const [, params] = context.queryKey;

    return Promise.resolve(
        studentDashboardQueryFn({
            ...context,
            queryKey: [
                STUDENT_DASHBOARD_QUERY_KEY,
                {
                    url: STUDENT_DASHBOARD_URL,
                    params,
                },
            ],
        }),
    );
};

type StudentDashboardContents = StudentDashboardShowContents;
const buildAccessGroupIds = (groups: { id: string }[]) => {
    const accessGroupIds = groups.map(group => group.id).filter((id): id is string => Boolean(id));
    return accessGroupIds;
};

type BuildStudentDashboardShowParamsArgs = {
    accessGroupIds: string[];
    prefLocale: string;
    contentViewsRefreshUpdatedAt: number | null;
    cohortId: string | undefined;
    institutionId: string;
};

export const buildStudentDashboardShowParams = (
    args: BuildStudentDashboardShowParamsArgs,
): StudentDashboardShowParams => {
    const { accessGroupIds, prefLocale, contentViewsRefreshUpdatedAt } = args;

    const baseParams = {
        'accessGroupIds[]': accessGroupIds,
        prefLocale,
        contentViewsRefreshUpdatedAt,
    };

    if (args.cohortId) {
        return {
            ...baseParams,
            cohortId: args.cohortId,
        };
    }

    if (args.institutionId) {
        return {
            ...baseParams,
            institutionId: args.institutionId,
        };
    }

    throw new Error('Student dashboard query requires either a cohort id or an institution id.');
};

export const useStudentDashboardQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const currentUser = useRequiredCurrentUser();
    const config = useConfig({ suspense: true });
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);

    const cohortId = cohort?.id;
    const institutionId = currentUser.activeInstitution?.id;

    const params = useMemo(
        () =>
            buildStudentDashboardShowParams({
                accessGroupIds: buildAccessGroupIds(currentUser.groups),
                prefLocale: currentUser.prefLocale,
                contentViewsRefreshUpdatedAt: config?.content_views_refresh_updated_at ?? null,
                cohortId,
                institutionId,
            }),
        [cohortId, institutionId, currentUser, config],
    );

    const queryKey = buildQueryKey(params);

    const baseOptions: UseQueryOptions<
        StudentDashboardShowResponse,
        Error,
        StudentDashboardContents,
        StudentDashboardQueryKey
    > = {
        queryKey,
        queryFn: runStudentDashboardQuery,
        enabled,

        // staleTime and gcTime have to be Infinity to keep this data available while offline. We rely on contentViewsRefreshUpdatedAt
        // to make sure it gets refetched
        staleTime: Infinity,
        gcTime: Infinity,
        select: ({ contents }) => contents,
    };

    return useQuery<StudentDashboardShowResponse, Error, StudentDashboardContents, StudentDashboardQueryKey>({
        ...baseOptions,
        ...(enabled ? ({ suspense: true } as const) : {}),
    } as UseQueryOptions<StudentDashboardShowResponse, Error, StudentDashboardContents, StudentDashboardQueryKey> & {
        suspense?: boolean;
    });
};

export const fetchStudentDashboard = (params: StudentDashboardShowParams) =>
    queryClient.fetchQuery<StudentDashboardShowResponse, Error, StudentDashboardShowResponse, StudentDashboardQueryKey>(
        {
            queryKey: buildQueryKey(params),
            queryFn: runStudentDashboardQuery,

            // See comment above staleTime
            staleTime: Infinity,
        },
    );
